import { HttpRequest } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { PersistentTokenStorageService } from "./persistent-token-storage.service";
import { PcpAuthService } from "./pcp-auth.service";
import { authServiceActions } from "../store/auth-service.actions";
import { ComponentStore } from "@ngrx/component-store";
import { ClientType, LiveLessonId } from "@pcp/core/interfaces";
import { filter, map, MonoTypeOperatorFunction, withLatestFrom } from "rxjs";

@Injectable({
    providedIn: 'root',
})
export class AuthServiceFacade extends ComponentStore<Record<string, never>> {

    private readonly authService = inject(PcpAuthService);

    readonly authenticated$ = this.authService.isAuthenticated$;
    readonly isAuthenticated$ = this.authService.isAuthenticated$;
    readonly isAuthenticatedOrUndefined$ = this.authService.isAuthenticatedOrUndefined$;

    readonly token$ = this.authService.accessToken$;
    readonly claims$ = this.authService.claims$;
    readonly customer$ = this.authService.customer$;
    readonly customerName$ = this.select(this.claims$, claims => claims?.customerName);
    readonly serials$ = this.select(this.claims$, claims => claims?.serials);
    readonly userName$ = this.select(this.claims$, claims => claims?.userName);
    readonly enetToken$ = this.select(this.claims$, claims => claims?.enetToken);
    readonly userId$ = this.authService.userId$;
    readonly sas$ = this.authService.sas$;
    readonly clientType$ = this.select(this.claims$, claims => claims?.clientType);

    readonly featureFlags$ = this.select(
        this.claims$,
        claims => claims?.featureFlags
    );

    /**
     * Access gate for the PracticeTrainer extension app.
     * Requires BOTH the per-user licence claim (`hasPracticeTrainer`) AND the
     * app-wide JWT feature flag (`featureFlags.practiceTrainerEnabled`) — either
     * alone is insufficient.
     */
    readonly canOpenPracticeTrainer$ = this.select(
        this.claims$,
        claims => Boolean(claims?.hasPracticeTrainer && claims?.featureFlags?.practiceTrainerEnabled),
    );

    /**
     * Access gate for the PushTheButton extension app.
     * Licence-gated only — there is no PTB feature flag in the JWT. The static
     * per-deployment PCP/TUT config flag gates UI visibility, but the licence
     * claim is the actual access boundary.
     */
    readonly canOpenPushTheButton$ = this.select(
        this.claims$,
        claims => Boolean(claims?.hasPushTheButton),
    );


    readonly authenticationFailed$ = this.authService.loginFailedReason$;
    readonly isLoggingIn$ = this.authService.isLoggingIn$;
    readonly loginFailedReason$ = this.authService.loginFailedReason$;
    readonly isInitialized$ = this.authService.isInitialized$;

    readonly loggedOut$ = this.authService.loggedOut$;

    constructor(
        private readonly tokenStorage: PersistentTokenStorageService,
        private readonly store: Store,
    ) {
        super({});
    }

    public clearToken(): void {
        this.tokenStorage.clear();
    }

    /**
     * RxJS gate for streams that must only be processed by specific client
     * types. Values pass through while the token's `ClientType` claim matches
     * one of `types`; everything else — including the pre-auth `undefined`
     * phase — is dropped.
     *
     * Use on transport-fed streams (SignalR/WebSocket) whose server-side
     * routing is keyed by the same claim: the gate encodes that invariant
     * client-side instead of relying on server routing behavior.
     */
    public gateByClientType<T>(...types: ClientType[]): MonoTypeOperatorFunction<T> {
        return source => source.pipe(
            withLatestFrom(this.clientType$),
            filter(([, clientType]) => clientType !== undefined && types.includes(clientType)),
            map(([value]) => value),
        );
    }

    public authWithSerials(serials: string[]): void {
        this.store.dispatch(authServiceActions.userLoginRequested({
            authData: {
                clientType: 'Teacher',
                method: 'Serials',
                credentials: { serials },
            }
        }));
    }

    public authWithCredentials(username: string, password: string): void {
        this.store.dispatch(authServiceActions.userLoginRequested({
            authData: {
                clientType: 'Teacher',
                method: 'UsernamePassword',
                credentials: { username, password },
            }
        }));
    }

    public authAsStudentWithRegistrationCodeAndEnetToken(studentRegistrationCode: string, enetToken: string): void {
        this.store.dispatch(authServiceActions.userLoginRequested({
            authData: {
                clientType: 'Student',
                method: 'StudentEnetToken',
                credentials: {
                    enetToken,
                    lessonCode: studentRegistrationCode,
                },
            }
        }));
    }

    public authWithLessonCode(beamerAuthCode: string): void {
        this.store.dispatch(authServiceActions.userLoginRequested({
            authData: {
                clientType: 'Beamer',
                method: 'BeamerAuthCode',
                credentials: { code: beamerAuthCode },
            }
        }));
    }

    public authLiveLessonModerator(liveLessonId: LiveLessonId, liveLessonLoginToken: string) {
        this.store.dispatch(authServiceActions.userLoginRequested({
            authData: {
                clientType: 'LiveLessonModerator',
                method: 'LiveLessonIdAndLoginToken',
                credentials: { liveLessonId, liveLessonLoginToken },
            }
        }));
    }
    authLiveLessonStudent(liveLessonId: string, liveLessonLoginToken: string) {
        this.store.dispatch(authServiceActions.userLoginRequested({
            authData: {
                clientType: 'LiveLessonStudent',
                method: 'LiveLessonIdAndLoginToken',
                credentials: { liveLessonId, liveLessonLoginToken },
            }
        }));
    }

    handleSignalrReconnect401() {
        this.store.dispatch(authServiceActions.tokenRefreshRequested());
    }


    public addToken(req: HttpRequest<any>): HttpRequest<any> {
        const token = this.tokenStorage.getAccessToken();

        if (!token) {
            return req;
        }

        return req.clone({
            headers: req.headers.set('Authorization', 'Bearer ' + token),
        });
    }

    public logout(): void {
        this.authService.logOut();
    }
}
